/**
 * Query Actions
 *
 * Actions related to querying data from tables and views.
 */

import { Table2, Edit } from 'lucide-react';
import type { ObjectActionDefinition, ObjectActionContext } from '../types';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { getScriptProvider } from '../../../scripting';
import { toast } from '../../../../components/ui/toaster';
import { SELECT_TOP_ROW_LIMIT } from '../../../scripting/selectTopRowLimit';
import { engineSupports, isValidEngineId, type DatabaseEngineId } from '../../../../core/engines';
import {
  queryEditorUnsupportedReason,
  tableExplorerUnsupportedReason,
} from '../../../main-toolbar/model/toolbarEngineFilters';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/** `undefined`/unknown engines resolve to mssql, matching `getConnectionById`. */
const engineIdOf = (engine: string): DatabaseEngineId =>
  isValidEngineId(engine) ? engine : 'mssql';

/**
 * Select Top 1000 action — opens a Query Editor tab with the SELECT statement
 * pre-filled and marked for auto-execution on mount. The QueryEditorTab drives
 * execution through the normal useQueryExecution flow so the loading state,
 * history entry, analytics event, and transaction handling all fire.
 */
export const selectTop1000Action: ObjectActionDefinition = {
  id: 'select-top-1000',
  label: `Select top ${SELECT_TOP_ROW_LIMIT} rows`,
  icon: Table2,
  category: 'query',
  appliesTo: ['table', 'view'],
  isPrimary: true,
  execute: async (context: ObjectActionContext) => {
    const { activeConnection, connection, engine } = getConnectionById(context.connectionId);

    if (!activeConnection || !connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }

    // Capability gate (S1 Task 1.16) — this action builds SQL and opens a
    // Query Editor tab itself, so it never passes the toolbar's gate.
    if (!engineSupports(engineIdOf(engine), 'workspaceFeatures', 'supportsQueryEditor')) {
      toast.error({
        title: 'Query Editor unavailable',
        description: queryEditorUnsupportedReason(engine),
      });
      return;
    }

    let query: string;
    try {
      const provider = getScriptProvider(engine);
      query = provider.generateTableScript('select-top', {
        database: context.databaseName,
        schema: context.schemaName,
        tableName: context.objectName,
      });
    } catch (error) {
      toast.error({
        title: 'Select Top Error',
        description: engine === 'sqlite'
          ? `Select top ${SELECT_TOP_ROW_LIMIT} rows is not supported for SQLite yet.`
          : error instanceof Error
            ? error.message
            : 'Failed to generate query',
      });
      return;
    }

    const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
    const queryNumber = getNextQueryNumber();

    addTab({
      title: `Query-${queryNumber}`,
      type: 'query',
      content: { query, autoExecuteOnMount: true },
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection.connection),
        database: context.databaseName,
      },
    });
  },
};

/**
 * Edit Top 200 action - opens table explorer in edit mode
 */
export const editTop200Action: ObjectActionDefinition = {
  id: 'edit-top-200',
  label: 'Edit top 200 rows',
  icon: Edit,
  category: 'query',
  appliesTo: ['table'],
  isPrimary: true,
  execute: (context: ObjectActionContext) => {
    const { activeConnection, connection, engine } = getConnectionById(context.connectionId);

    if (!activeConnection || !connection) {
      toast.error({ title: 'Error', description: 'Connection not found' });
      return;
    }

    // Capability gate (S1 Task 1.16) — this action calls `addTab` directly,
    // bypassing `openTableExplorerTab` and its guard.
    if (!engineSupports(engineIdOf(engine), 'workspaceFeatures', 'supportsTableExplorer')) {
      toast.error({
        title: 'Table Explorer unavailable',
        description: tableExplorerUnsupportedReason(engine),
      });
      return;
    }

    const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
    const resolvedSchema = context.schemaName ?? defaultSchema;

    const { addTab } = useWorkspaceStore.getState();

    addTab({
      title: `${context.objectName} [Edit]`,
      type: 'table-explorer',
      content: {
        tableName: context.objectName,
        schema: resolvedSchema,
        database: context.databaseName,
        rows: [],
        columns: [],
        limit: 200,
        selectedColumns: '*',
        filters: [],
        sorting: [],
        isLoading: true,
      },
      connectionInfo: {
        connectionId: context.connectionId,
        connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
        serverHost: safeConnectionServer(activeConnection.connection),
        database: context.databaseName,
      },
    });
  },
};

/**
 * All query actions
 */
export const queryActions: ObjectActionDefinition[] = [
  selectTop1000Action,
  editTop200Action,
];
